import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
} from '@angular/core';
import { TuiIcon } from '@taiga-ui/core';
import * as Mousetrap from 'mousetrap';

type IconKey = 'command' | (string & {});

const IconKeyMap: { [key in IconKey]?: string } = {
	command: '@tui.command',
};

@Component({
	imports: [TuiIcon],
	selector: 'ctx-hotkey',
	styleUrl: 'hotkey.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		@if (icon(); as icon) {
			<tui-icon [icon]="icon" />
		} @else {
			{{ text() }}
		}
	`,
})
export class HotkeyComponent implements OnDestroy {
	private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

	readonly key = input.required<IconKey>();

	readonly allowEvents = input(true);

	private readonly cdRef = inject(ChangeDetectorRef);

	readonly icon = computed(() => {
		const key = this.key();
		return IconKeyMap[key];
	});

	readonly text = computed(() => {
		const icon = this.icon();
		if (icon) return null;
		return this.key();
	});

	keydownEvent!: Mousetrap.MousetrapInstance;
	keyupEvent!: Mousetrap.MousetrapInstance;
	abortController = new AbortController();

	constructor() {
		effect(() => {
			if (!this.allowEvents()) return;

			const key = this.key();
			this.setupEvents(key);
		});
	}

	ngOnDestroy() {
		const key = this.key().toLowerCase();
		this.keyupEvent.unbind([key]);
		this.keydownEvent.unbind([key]);
		this.abortController.abort();
	}

	setupEvents(key: IconKey) {
		key = key.toLowerCase().trim();
		this.keydownEvent = Mousetrap.bind(
			key,
			this.setPress.bind(this, true),
			'keydown',
		);

		this.keyupEvent = Mousetrap.bind(
			key,
			this.setPress.bind(this, false),
			'keyup',
		);

		document.addEventListener('focusin', this.setPress.bind(this, false), {
			signal: this.abortController.signal,
		});
	}

	setPress(value: boolean) {
		if (value) this.el.nativeElement.classList.add('pressed');
		else this.el.nativeElement.classList.remove('pressed');
		this.cdRef.detectChanges();
	}
}
